import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import ThemeToggle from "@/components/ThemeToggle";
import { Spinner } from "@/components/Kit";
import { Cpu, Mail, Lock, User } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password, remember);
      else await register(form.email, form.password, form.name, remember);
      toast.success("Station link established.");
      navigate("/app/station");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Auth failed"); }
    finally { setBusy(false); }
  };

  const google = () => {
    const redirectUrl = window.location.origin + "/app/station";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid-bg grid place-items-center p-4 relative overflow-hidden">
      <div className="absolute top-4 right-4"><ThemeToggle /></div>
      <div className="w-full max-w-md panel-glow p-8 pop-in hud-corner">
        <div className="text-center mb-6">
          <span className="inline-grid place-items-center w-14 h-14 rounded-[var(--radius)] bg-primary text-primary-foreground mb-3"><Cpu className="w-8 h-8" /></span>
          <h1 className="font-head font-black text-3xl neon-text tracking-widest">ORBIT</h1>
          <p className="label-cap mt-1">Space Station Terminal // Access Required</p>
        </div>

        <div className="flex gap-1 mb-6 p-1 rounded-[var(--radius)] bg-muted">
          {["login", "register"].map((m) => (
            <button key={m} data-testid={`tab-${m}`} onClick={() => setMode(m)}
              className={`flex-1 py-2 rounded-[var(--radius)] text-xs font-bold uppercase tracking-wider mono transition-colors ${mode === m ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
              {m === "login" ? "Sign in" : "Register"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (
            <div className="relative"><User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input data-testid="name-input" required value={form.name} onChange={upd("name")} placeholder="Callsign" className="input-field pl-10" /></div>
          )}
          <div className="relative"><Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input data-testid="email-input" type="email" required value={form.email} onChange={upd("email")} placeholder="pilot@orbit.online" className="input-field pl-10" /></div>
          <div className="relative"><Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input data-testid="password-input" type="password" required minLength={6} value={form.password} onChange={upd("password")} placeholder="Access code (min 6)" className="input-field pl-10" /></div>
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
            <input data-testid="remember-me" type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="w-4 h-4 accent-[hsl(var(--primary))]" /> Keep me logged in
          </label>
          <button type="submit" data-testid="submit-auth-btn" disabled={busy} className="btn-primary w-full py-3">
            {busy ? <Spinner className="w-4 h-4" /> : mode === "login" ? "Connect" : "Create account"}
          </button>
        </form>

        <div className="flex items-center gap-3 my-4"><div className="h-px flex-1 bg-border" /><span className="label-cap">or</span><div className="h-px flex-1 bg-border" /></div>
        <button onClick={google} data-testid="google-login-btn" className="btn-ghost w-full py-3">
          <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" /> Continue with Google
        </button>
      </div>
    </div>
  );
}
